import { useState, type FC } from 'react'
import {
    Modal,
    Stack,
    Group,
    Text,
    Button,
    SegmentedControl,
    TextInput,
    PasswordInput,
    Alert,
} from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import {
    directConnectionFields,
    findDestination,
    marketplaceConnectionFields,
    MARKETPLACE_PROVIDER_LABEL,
} from '@/lib/marketplaceDestinations'
import type {
    ConfigField,
    ConfigSlotRef,
    MarketplaceProvider,
} from '@/lib/marketplaceDestinations'
import { useSegmentStubs } from '../queries'
import type { Destination } from '../types'

export interface DestinationConnectionModalProps {
    destination: Destination
    opened: boolean
    onClose: () => void
}

// Set up (persist) a destination's *connection* — the direct platform's
// credentials, or a marketplace account that carries it. This is the connection
// slot only; per-delivery setup lives in DistributionConfigModal. Saving merges
// partial values via saveDestinationConfig, so connection readiness
// (destinationConnectionReady) flips once the required fields are filled.
export const DestinationConnectionModal: FC<
    DestinationConnectionModalProps
> = ({ destination, opened, onClose }) => {
    const { savedConfig, saveDestinationConfig } = useSegmentStubs()
    const catalog = findDestination(destination.platform)
    const isDirect = catalog?.direct ?? false
    const providers = catalog && !catalog.direct ? catalog.indirectVia : []

    const [provider, setProvider] = useState<MarketplaceProvider | undefined>(
        providers[0]
    )
    // Field values across every connectable slot, seeded from what's saved.
    const [values, setValues] = useState<Record<string, string>>(() => {
        const seeded: Record<string, string> = {}
        if (!catalog) return seeded
        if (catalog.direct) {
            const saved = savedConfig.direct(destination.platform) ?? {}
            for (const field of directConnectionFields(catalog)) {
                seeded[`direct.${field.key}`] = saved[field.key] ?? ''
            }
        } else {
            for (const via of catalog.indirectVia) {
                const saved = savedConfig.marketplace(via) ?? {}
                for (const field of marketplaceConnectionFields[via]) {
                    seeded[`m:${via}.${field.key}`] = saved[field.key] ?? ''
                }
            }
        }
        return seeded
    })

    const slotKey = isDirect ? 'direct' : provider ? `m:${provider}` : ''
    const fields: ConfigField[] = !catalog
        ? []
        : isDirect
          ? directConnectionFields(catalog)
          : provider
            ? marketplaceConnectionFields[provider]
            : []

    const handleSave = () => {
        if (catalog && slotKey) {
            const ref: ConfigSlotRef = isDirect
                ? { kind: 'direct', destination: destination.platform }
                : { kind: 'marketplace', provider: provider! }
            const slotValues: Record<string, string> = {}
            for (const field of fields) {
                const value = (values[`${slotKey}.${field.key}`] ?? '').trim()
                if (value) slotValues[field.key] = value
            }
            if (Object.keys(slotValues).length > 0) {
                saveDestinationConfig(ref, slotValues)
            }
        }
        onClose()
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={600}>Connect {destination.platform}</Text>}
            centered
            size='md'
        >
            <Stack gap='md'>
                {!catalog ? (
                    <Alert
                        color='gray'
                        icon={<IconInfoCircle size={18} />}
                        title='No connection needed'
                    >
                        This destination doesn&apos;t require connection setup.
                    </Alert>
                ) : (
                    <>
                        {isDirect ? (
                            <Text size='sm' c='dimmed'>
                                {destination.platform} has a direct integration
                                — enter its credentials to connect.
                            </Text>
                        ) : (
                            <>
                                {providers.length > 1 && (
                                    <div>
                                        <Text size='sm' fw={500} mb={6}>
                                            Connect through
                                        </Text>
                                        <SegmentedControl
                                            value={provider}
                                            onChange={(v) =>
                                                setProvider(
                                                    v as MarketplaceProvider
                                                )
                                            }
                                            data={providers.map((via) => ({
                                                value: via,
                                                label: MARKETPLACE_PROVIDER_LABEL[
                                                    via
                                                ],
                                            }))}
                                        />
                                    </div>
                                )}
                                <Text size='sm' c='dimmed'>
                                    Connects your{' '}
                                    {provider
                                        ? MARKETPLACE_PROVIDER_LABEL[provider]
                                        : ''}{' '}
                                    account, shared across every destination
                                    reached through it.
                                </Text>
                            </>
                        )}

                        {fields.map((field) => {
                            const ck = `${slotKey}.${field.key}`
                            const Comp =
                                field.type === 'secret'
                                    ? PasswordInput
                                    : TextInput
                            return (
                                <Comp
                                    key={ck}
                                    label={field.label}
                                    placeholder={field.placeholder}
                                    value={values[ck] ?? ''}
                                    onChange={(e) => {
                                        const value = e.currentTarget.value
                                        setValues((prev) => ({
                                            ...prev,
                                            [ck]: value,
                                        }))
                                    }}
                                />
                            )
                        })}
                    </>
                )}

                <Group justify='flex-end' mt='xs'>
                    <Button variant='default' onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>Save connection</Button>
                </Group>
            </Stack>
        </Modal>
    )
}
